import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CheckCircle2,
  ChevronRight,
  ClockAlert,
  CreditCard,
  PackageSearch,
  TriangleAlert,
  Undo2,
} from 'lucide-react'
import styles from './NeedAttention.module.css'
import { fmtInt } from '../../../utils/moneyFormat'

// Operational action queue (data from /dashboard/overview → needsAttention).
// Order is deliberate: supplier-side blockers first, money-side last.
const QUEUE = [
  {
    key: 'unconfirmedProducts',
    label: 'Unconfirmed products',
    sub: 'Awaiting supplier confirmation',
    to: '/bookings',
    icon: PackageSearch,
    tone: 'warn',
  },
  {
    key: 'pendingBookings',
    label: 'Bookings pending',
    sub: 'Not yet confirmed',
    to: '/bookings',
    icon: ClockAlert,
    tone: 'warn',
  },
  {
    key: 'unpaidDeparted',
    label: 'Unpaid after departure',
    sub: 'Travelled with a balance due',
    to: '/bookings',
    icon: TriangleAlert,
    tone: 'neg',
  },
  {
    key: 'failedPayments',
    label: 'Failed payments',
    sub: 'Needs a retry or write-off',
    to: '/finance/payments/incoming',
    icon: CreditCard,
    tone: 'neg',
  },
  {
    key: 'pendingRefunds',
    label: 'Refunds to pay out',
    sub: 'Approved, awaiting payout',
    to: '/finance/payments/refunds',
    icon: Undo2,
    tone: 'accent',
  },
]

// Tone → chip class. There is no global negative chip, so that one lives in
// the module stylesheet.
const chipClass = (tone) => {
  if (tone === 'accent') return 'dshChipAccent'
  if (tone === 'neg') return styles.chipNeg
  return 'dshChipWarn'
}

const NeedsAttention = ({ data, loading }) => {
  const navigate = useNavigate()

  const rows = useMemo(
    () => QUEUE.map((item) => ({ ...item, count: Math.max(0, Number(data?.[item.key]) || 0) })),
    [data]
  )

  const total = rows.reduce((sum, row) => sum + row.count, 0)
  const allClear = !loading && total === 0

  const go = (to) => () => navigate(to)

  const onKeyDown = (to) => (e) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault()
      navigate(to)
    }
  }

  return (
    <div className={`dshPanel ${styles.root}`}>
      <div className="dshHead">
        <div>
          <h3 className="dshTitle">Needs Attention</h3>
          <p className="dshSub">Operational queue</p>
        </div>
        {loading ? (
          <span className={`dshSkel ${styles.skelPill}`} />
        ) : (
          <span className={`dshPill dshNum ${total > 0 ? 'dshPillWarn' : 'dshPillPos'}`}>
            {total > 0 ? `${fmtInt(total)} open` : 'All clear'}
          </span>
        )}
      </div>

      <div className={`dshBody ${styles.body} ${allClear ? styles.bodyClear : ''}`}>
        {loading &&
          [0, 1, 2, 3, 4].map((i) => (
            <div className="dshRow" key={i}>
              <div className={styles.left}>
                <span className={`dshSkel ${styles.skelChip}`} />
                <div className={styles.skelText}>
                  <span className={`dshSkel ${styles.skelLabel}`} />
                  <span className={`dshSkel ${styles.skelSub}`} />
                </div>
              </div>
              <span className={`dshSkel ${styles.skelCount}`} />
            </div>
          ))}

        {allClear && (
          <div className={styles.clear}>
            <span className={styles.clearMark}>
              <CheckCircle2 size={22} strokeWidth={2} />
            </span>
            <p className={styles.clearTitle}>Nothing needs attention</p>
            <p className={styles.clearNote}>Every queue is clear right now.</p>
          </div>
        )}

        {!loading &&
          !allClear &&
          rows.map((row) => {
            const Icon = row.icon
            const active = row.count > 0
            const interactive = active
              ? {
                  role: 'button',
                  tabIndex: 0,
                  onClick: go(row.to),
                  onKeyDown: onKeyDown(row.to),
                  'aria-label': `${row.label}: ${fmtInt(row.count)}`,
                }
              : {}

            return (
              <div
                key={row.key}
                className={`dshRow ${active ? 'dshRowLink' : styles.rested}`}
                {...interactive}
              >
                <div className={styles.left}>
                  <span className={`dshChip ${chipClass(row.tone)} ${styles.chip}`}>
                    <Icon size={15} strokeWidth={2} />
                  </span>
                  <div className={styles.text}>
                    <span className={`${styles.label} dshTrunc`}>{row.label}</span>
                    <span className={`${styles.sub} dshTrunc`}>{row.sub}</span>
                  </div>
                </div>
                <div className={styles.right}>
                  <span
                    className={`dshNum ${styles.count} ${active ? '' : styles.countZero}`}
                  >
                    {fmtInt(row.count)}
                  </span>
                  <ChevronRight size={15} strokeWidth={2} className={styles.chevron} />
                </div>
              </div>
            )
          })}
      </div>
    </div>
  )
}

export default NeedsAttention
